import React, { useEffect, useState, useCallback } from 'react';
import { Modal, ModalBody } from '../common';

export interface NewAchievement {
  id: number;
  name: string;
  description: string;
  icon: string;
}

interface AchievementUnlockModalProps {
  /** List of new achievements to display */
  achievements: NewAchievement[];
  /** URL to mark achievements as seen */
  markSeenUrl: string;
  /** CSRF token for API requests */
  csrfToken: string;
  /** Translations for modal content */
  translations: {
    achievementUnlocked: string;
    achievementsUnlocked: string;
    continueButton: string;
  };
}

/**
 * AchievementUnlockModal - Displays newly unlocked achievements to the user
 *
 * Ported from:
 * - app/views/diaries/shared/_achievement_unlock_modal.html.erb
 * - app/javascript/controllers/achievement_modal_controller.js
 *
 * Features:
 * - Single achievement display
 * - Carousel for multiple achievements (custom implementation, no Bootstrap)
 * - Session storage to prevent duplicate displays
 * - Auto-mark achievements as seen on close
 */
export function AchievementUnlockModal({
  achievements,
  markSeenUrl,
  csrfToken,
  translations,
}: AchievementUnlockModalProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  // Check session storage to avoid showing the same modal twice
  useEffect(() => {
    if (achievements.length === 0) return;

    const sortedIds = [...achievements.map((a) => a.id)].sort().join(',');
    const sessionKey = `achievement_modal_shown_${sortedIds}`;

    if (sessionStorage.getItem(sessionKey)) {
      return;
    }

    sessionStorage.setItem(sessionKey, 'true');
    setIsOpen(true);
  }, [achievements]);

  const handlePreviousSlide = useCallback(() => {
    setActiveIndex((prev) => (prev === 0 ? achievements.length - 1 : prev - 1));
  }, [achievements.length]);

  const handleNextSlide = useCallback(() => {
    setActiveIndex((prev) => (prev === achievements.length - 1 ? 0 : prev + 1));
  }, [achievements.length]);

  const handleGoToSlide = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  const markAllAsSeen = useCallback(() => {
    if (!markSeenUrl || achievements.length === 0) return;

    fetch(markSeenUrl, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({ ids: achievements.map((a) => a.id) }),
    });
  }, [markSeenUrl, achievements, csrfToken]);

  const handleContinue = useCallback(() => {
    markAllAsSeen();
    setIsOpen(false);
  }, [markAllAsSeen]);

  // This modal should not close on backdrop click or escape
  const handleClose = useCallback(() => {
    // Do nothing - user must click continue
  }, []);

  if (achievements.length === 0 || !isOpen) {
    return null;
  }

  const renderAchievementContent = (achievement: NewAchievement) => (
    <div className="mb-4">
      <div className="d-inline-block position-relative">
        <div
          className="rounded-circle d-flex align-items-center justify-content-center bg-warning bg-opacity-10 mx-auto"
          style={{ width: 120, height: 120 }}
        >
          <i className={`bi bi-${achievement.icon} text-warning`} style={{ fontSize: '4rem' }}></i>
        </div>
        <span className="position-absolute top-0 end-0 badge rounded-circle bg-success p-2">
          <i className="bi bi-check-lg fs-5"></i>
        </span>
      </div>
    </div>
  );

  const renderSingleAchievement = () => {
    const achievement = achievements[0];
    return (
      <>
        {renderAchievementContent(achievement)}
        <h3 className="mb-3 fw-bold">{translations.achievementUnlocked}!</h3>
        <h4 className="text-primary mb-3">{achievement.name}</h4>
        <p className="text-muted mb-4">{achievement.description}</p>
      </>
    );
  };

  const renderCarousel = () => {
    const currentAchievement = achievements[activeIndex];
    return (
      <>
        {/* Carousel content */}
        <div style={{ minHeight: 200 }}>
          {renderAchievementContent(currentAchievement)}
          <h4 className="text-primary mb-3">{currentAchievement.name}</h4>
          <p className="text-muted mb-0">{currentAchievement.description}</p>
        </div>

        {/* Carousel controls */}
        <div className="d-flex justify-content-center align-items-center gap-3 mt-4 mb-4">
          <button
            className="btn btn-outline-secondary btn-sm rounded-circle p-2"
            type="button"
            onClick={handlePreviousSlide}
            aria-label="Previous achievement"
          >
            <i className="bi bi-chevron-left"></i>
          </button>
          <div className="d-flex gap-2">
            {achievements.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleGoToSlide(index)}
                className="rounded-circle"
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: index === activeIndex ? 'var(--bs-primary)' : 'var(--bs-gray-300)',
                  border: 'none',
                  cursor: 'pointer',
                }}
                aria-current={index === activeIndex ? 'true' : undefined}
                aria-label={`Achievement ${index + 1}`}
              />
            ))}
          </div>
          <button
            className="btn btn-outline-secondary btn-sm rounded-circle p-2"
            type="button"
            onClick={handleNextSlide}
            aria-label="Next achievement"
          >
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>

        <h3 className="mb-3 fw-bold">
          {achievements.length} {translations.achievementsUnlocked}!
        </h3>
      </>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      closeOnBackdropClick={false}
      closeOnEscape={false}
      ariaLabel={translations.achievementUnlocked}
      centered
    >
      <ModalBody className="text-center p-5">
        {achievements.length > 1 ? renderCarousel() : renderSingleAchievement()}

        <button type="button" className="btn btn-primary btn-lg px-5" onClick={handleContinue}>
          {translations.continueButton}
        </button>
      </ModalBody>
    </Modal>
  );
}
